import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const groupMembers = [
    {
      name: 'Randall Roberts',
      role: 'Technical Support Lead',
      specialization: 'Online payment platform and technical issues',
      email: 'randall.roberts@mycavehill.uwi.edu'
    },
    {
      name: 'Whennecia Lockhart',
      role: 'Banking & Transfer Specialist', 
      specialization: 'Bank transfers and international payments',
      email: 'whennecia.lockhart@mycavehill.uwi.edu'
    },
    {
      name: 'Rashida Mayers',
      role: 'Deadline & Policy Advisor',
      specialization: 'Payment deadlines and university policies',
      email: 'rashida.mayers1@mycavehill.uwi.edu'
    },
    {
      name: 'Kaleah Blackman',
      role: 'Project Manager & Payment Systems Coordinator',
      specialization: 'Project coordination and payment processing inquiries',
      email: 'kaleah.blackman@mycavehill.uwi.edu'
    },
    {
      name: 'Aasiyah Brathwaite',
      role: 'Student Accounts Specialist',
      specialization: 'Account balance and fee-related questions',
      email: 'aasiyah.brathwaite@mycavehill.uwi.edu'
    }
  ];

  const teamMembers = [
    { name: 'Kaleah Blackman', id: '400017713' },
    { name: 'Aasiyah Brathwaite', id: '400020876' },
    { name: 'Randall Roberts', id: '320088929' },
    { name: 'Whennecia Lockhart', id: '400020264' },
    { name: 'Rashida Mayers', id: '400017960' }
  ];

  const quickLinks = [
    {
      title: 'How to Pay Fees',
      description: 'Step-by-step payment instructions',
      href: '/how-to-pay',
      icon: 'ri-bank-card-line',
      color: 'bg-blue-500'
    },
    {
      title: 'Bank Details',
      description: 'Account information for payments',
      href: '/bank-details',
      icon: 'ri-bank-line',
      color: 'bg-green-500'
    },
    {
      title: 'Payment Deadlines',
      description: 'Important dates and timelines',
      href: '/deadlines',
      icon: 'ri-calendar-line',
      color: 'bg-orange-500'
    },
    {
      title: 'Contact Support',
      description: 'Get help with your payments',
      href: '/contact',
      icon: 'ri-customer-service-line',
      color: 'bg-purple-500'
    }
  ];

  const navigation = [
    { name: 'Home', href: '/', icon: 'ri-home-line' },
    { name: 'How to Pay', href: '/how-to-pay', icon: 'ri-bank-card-line' },
    { name: 'Bank Details', href: '/bank-details', icon: 'ri-bank-line' },
    { name: 'Deadlines', href: '/deadlines', icon: 'ri-calendar-line' },
    { name: 'Contact Us', href: '/contact', icon: 'ri-customer-service-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/5b778ac9459a5e5a57535db275ea181c.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-blue-900/70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              MGMT University – Student Payments Support Portal
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed">
              Welcome to the MGMT University Student Payments Help Portal. This website was created to help students clearly understand how to pay tuition and other related fees. Here you will find step-by-step instructions, banking details, and important deadlines. Our goal is to ensure you complete payments correctly, efficiently, and on time — without confusion or unnecessary delays.
            </p>
            <p className="text-lg mb-12 text-blue-100">
              Streamlining student payments for a smoother academic experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/how-to-pay"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Start Payment Process
              </Link>
              <Link
                to="/deadlines"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                View Deadlines
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-green-500 rounded-full blur-2xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full mb-8 shadow-lg">
              <i className="ri-compass-3-line mr-3 text-xl"></i>
              <span className="font-bold text-lg">Quick Access Portal</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              Navigate Your Payment Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need to complete your university payments efficiently and on time
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-8 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {quickLinks.map((link, index) => {
              const gradients = [
                'from-blue-500 to-blue-600',
                'from-green-500 to-emerald-600',
                'from-orange-500 to-red-500',
                'from-purple-500 to-pink-600'
              ];
              const bgPatterns = [
                'bg-blue-50',
                'bg-green-50', 
                'bg-orange-50',
                'bg-purple-50'
              ];
              const delays = ['delay-0', 'delay-75', 'delay-150', 'delay-225'];
              
              return (
                <Link
                  key={index}
                  to={link.href}
                  className={`group relative bg-white rounded-2xl p-10 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden ${delays[index]} animate-fade-in-up`}
                >
                  {/* Background Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index]} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  
                  {/* Floating Background Elements */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-white to-gray-100 rounded-full opacity-20 group-hover:scale-150 transition-transform duration-700"></div>
                  <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full opacity-30 group-hover:scale-125 transition-transform duration-500"></div>
                  
                  <div className="relative z-10">
                    {/* Icon Container */}
                    <div className="relative mb-8">
                      <div className={`w-20 h-20 bg-gradient-to-br ${gradients[index]} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                        <i className={`${link.icon} text-white text-3xl`}></i>
                      </div>
                      {/* Icon Glow Effect */}
                      <div className={`absolute inset-0 w-20 h-20 bg-gradient-to-br ${gradients[index]} rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors">
                      {link.title}
                    </h3>
                    <p className="text-gray-600 mb-8 leading-relaxed group-hover:text-gray-700 transition-colors">
                      {link.description}
                    </p>
                    
                    {/* Action Button */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-blue-600 group-hover:text-blue-700 font-semibold transition-colors">
                        <span className="text-sm">Explore Now</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform duration-300"></i>
                      </div>
                      
                      {/* Progress Indicator */}
                      <div className="w-8 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${gradients[index]} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700`}></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Border Effect */}
                  <div className={`absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-r ${gradients[index]} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} style={{mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', maskComposite: 'xor'}}></div>
                </Link>
              );
            })}
          </div>
          
          {/* Bottom Stats */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-shield-check-line text-white text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">100%</div>
              <div className="text-sm text-gray-600">Secure Payments</div>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">24/7</div>
              <div className="text-sm text-gray-600">Portal Access</div>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-bank-line text-white text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">4</div>
              <div className="text-sm text-gray-600">Payment Methods</div>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-customer-service-2-line text-white text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">5</div>
              <div className="text-sm text-gray-600">Support Specialists</div>
            </div>
          </div>
        </div>
      </section>

      {/* Group Information */}
      <section className="py-20 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Contact Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our dedicated team is here to assist you with all your payment-related questions and concerns
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {groupMembers.map((member, index) => {
              const colors = [
                'from-blue-500 to-blue-600',
                'from-purple-500 to-purple-600', 
                'from-green-500 to-green-600',
                'from-orange-500 to-orange-600',
                'from-pink-500 to-pink-600'
              ];
              const icons = [
                'ri-settings-3-line',
                'ri-bank-line',
                'ri-calendar-check-line',
                'ri-user-star-line',
                'ri-user-settings-line'
              ];
              const teamMember = teamMembers.find(tm => tm.name === member.name);
              
              return (
                <div key={index} className="group">
                  <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                    <div className="text-center">
                      {index === 0 ? (
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          <img 
                            src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/f19c80d99d21f30c768f6e6e5b8e595d.jpeg"
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : index === 1 ? (
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          <img 
                            src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/3d8fd4ed5fc75714fcfa144e9296a7ff.jpeg"
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : index === 3 ? (
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          <img 
                            src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/f1474470776e6791abe53d486bbafc5a.jpeg"
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : index === 4 ? (
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          <img 
                            src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/a246b57cff446bca1b4a9c274b3f3d5b.jpeg"
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : index === 2 ? (
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          <img 
                            src="https://static.readdy.ai/image/7acfccf655b34ed909342c128b73c861/c9d1dcefded1d93b07130d08fa804102.jpeg"
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      ) : (
                        <div className={`w-20 h-20 bg-gradient-to-br ${colors[index]} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <i className={`${icons[index]} text-white text-3xl`}></i>
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {member.name}
                        {teamMember && (
                          <span className="block text-sm font-normal text-blue-600 mt-1">
                            ID: {teamMember.id}
                          </span>
                        )}
                      </h3>
                      <div className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                        {member.role}
                      </div>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed">{member.specialization}</p>
                      
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                          <i className="ri-code-line text-green-500"></i>
                          <span>Web Development</span>
                        </div>
                      </div>
                      
                      <div className="pt-6 border-t border-gray-100">
                        <div className="flex justify-center space-x-3">
                          <a
                            href={`mailto:${member.email}`}
                            className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors cursor-pointer group/btn"
                            title={`Email ${member.name}`}
                          >
                            <i className="ri-mail-line text-blue-600 text-lg group-hover/btn:scale-110 transition-transform"></i>
                          </a>
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors cursor-pointer group/btn">
                            <i className="ri-phone-line text-green-600 text-lg group-hover/btn:scale-110 transition-transform"></i>
                          </div>
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center hover:bg-purple-200 transition-colors cursor-pointer group/btn">
                            <i className="ri-user-line text-purple-600 text-lg group-hover/btn:scale-110 transition-transform"></i>
                          </div>
                        </div>
                        <div className="mt-3">
                          <span className="text-xs text-gray-500">Contact for {member.role.split(' ')[0]} support</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Team Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-blue-600 text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">5</div>
              <div className="text-sm text-gray-600">Team Members</div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-line text-green-600 text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">1</div>
              <div className="text-sm text-gray-600">Course Project</div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-pages-line text-purple-600 text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">4</div>
              <div className="text-sm text-gray-600">Portal Pages</div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg text-center border border-gray-100">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-bank-card-line text-orange-600 text-xl"></i>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">4</div>
              <div className="text-sm text-gray-600">Payment Methods</div>
            </div>
          </div>

          {/* Team Achievement Banner */}
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center shadow-2xl">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-4">
                <i className="ri-trophy-line text-4xl text-yellow-300 mr-3"></i>
                <h3 className="text-2xl md:text-3xl font-bold">MGMT2005 Excellence</h3>
              </div>
              <p className="text-lg md:text-xl text-blue-100 leading-relaxed">
                "Demonstrating innovation in microcomputer applications through collaborative web development and user-centered design for university payment systems."
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <div className="bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold">Web Development</span>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold">User Experience</span>
                </div>
                <div className="bg-white/20 px-4 py-2 rounded-full">
                  <span className="text-sm font-semibold">Business Applications</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">About This Portal</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Academic Project</h3>
                    <p className="text-gray-700 leading-relaxed">
                      This portal was developed by a student project group within the MGMT2005 Microcomputer Applications for Business course.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-target-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Objective</h3>
                    <p className="text-gray-700 leading-relaxed">
                      To simplify the student payment process by presenting fee-related information in a clear, structured, and digital format.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation Impact</h3>
                    <p className="text-gray-700 leading-relaxed">
                      This system demonstrates how an online platform can improve communication, reduce administrative bottlenecks, and help students avoid late charges.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="relative">
              <div 
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Modern%20university%20students%20working%20together%20on%20laptops%20in%20bright%20study%20area%2C%20collaborative%20learning%20environment%20with%20technology%2C%20diverse%20group%20of%20students%20engaged%20in%20academic%20project%20work%2C%20professional%20educational%20setting&width=600&height=400&seq=students-project&orientation=landscape')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  height: '400px'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <h4 className="text-xl font-semibold mb-2">Empowering Student Success</h4>
                  <p className="text-sm opacity-90">
                    Making university payments simple, transparent, and stress-free for every student.
                  </p>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">5</div>
                  <div className="text-sm text-gray-600">Team Members</div>
                </div>
              </div>

              <div className="absolute -top-6 -left-6 bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">4</div>
                  <div className="text-sm text-gray-600">Payment Methods</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Features */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-blue-600 text-2xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Secure Payments</h4>
              <p className="text-gray-600 text-sm">Bank-grade security for all your transactions</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-green-600 text-2xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">24/7 Access</h4>
              <p className="text-gray-600 text-sm">Access payment information anytime, anywhere</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-purple-600 text-2xl"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Expert Support</h4>
              <p className="text-gray-600 text-sm">Dedicated team ready to assist you</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
